import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useReactToPrint } from 'react-to-print';
import {
  productionLotService,
  lotProgressService,
  workPhaseService,
  workCycleService,
  qmsCheckService,
  controlPlanService,
  nonConformityService,
  attachmentService,
  revisionToolService,
  deliveryService,
  simplifiedProgressService,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import PrintableLot from '../components/PrintableLot';
import PrintablePhaseSheets from '../components/PrintablePhaseSheets';
import { LotHeader } from '../components/lot-detail/LotHeader';
import { LotOverviewCards } from '../components/lot-detail/LotOverviewCards';
import { LotTabs } from '../components/lot-detail/LotTabs';
import { ProgressDialog, StatusDialog, DeliveryDialog, SimplifiedProgressDialog } from '../components/lot-detail/LotDialogs';

const initialProgressForm = {
  work_phase_id: '',
  operator: '',
  machine: '',
  good_quantity: '',
  scrap_quantity: '0',
  rework_quantity: '0',
  actual_time_minutes: '',
  notes: '',
};

const initialDeliveryForm = () => ({
  delivery_date: new Date().toISOString().split('T')[0],
  delivered_quantity: '',
  ddt_reference: '',
  notes: '',
});

const initialSimpleForm = () => ({
  progress_date: new Date().toISOString().split('T')[0],
  produced_quantity: '',
  delivered_quantity: '',
  ddt_reference: '',
  notes: '',
});

export default function LotDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();

  // Data
  const [lot, setLot] = useState(null);
  const [progress, setProgress] = useState([]);
  const [workPhases, setWorkPhases] = useState([]);
  const [controls, setControls] = useState([]);
  const [controlPlans, setControlPlans] = useState([]);
  const [ncs, setNcs] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [revisionTools, setRevisionTools] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [simplifiedProgress, setSimplifiedProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('progress');

  // Print
  const [printData, setPrintData] = useState(null);
  const [triggerPrint, setTriggerPrint] = useState(false);
  const [triggerPhasePrint, setTriggerPhasePrint] = useState(false);
  const printRef = useRef(null);
  const phasePrintRef = useRef(null);

  // Dialogs
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [simpleDialogOpen, setSimpleDialogOpen] = useState(false);
  const [progressForm, setProgressForm] = useState(initialProgressForm);
  const [deliveryForm, setDeliveryForm] = useState(initialDeliveryForm);
  const [simpleForm, setSimpleForm] = useState(initialSimpleForm);
  const [newStatus, setNewStatus] = useState('');

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Commessa_${lot?.lot_code || ''}`,
    pageStyle: '@page { size: A4 portrait; margin: 8mm; } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }',
  });
  const handlePrintPhases = useReactToPrint({
    contentRef: phasePrintRef,
    documentTitle: `Commessa_${lot?.lot_code || ''}_per_Fasi`,
    pageStyle: '@page { size: A4 portrait; margin: 8mm; } @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }',
  });

  useEffect(() => {
    if (triggerPrint && printData) {
      const timer = setTimeout(() => {
        handlePrint();
        setTriggerPrint(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [triggerPrint, printData, handlePrint]);

  useEffect(() => {
    if (triggerPhasePrint && printData) {
      const timer = setTimeout(() => {
        handlePrintPhases();
        setTriggerPhasePrint(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [triggerPhasePrint, printData, handlePrintPhases]);

  const loadLot = useCallback(async () => {
    try {
      const lotRes = await productionLotService.getById(id);
      setLot(lotRes.data);
      const lotCode = lotRes.data?.lot_code;
      const [progressRes, controlsRes, ncsRes, attachmentsRes] = await Promise.all([
        lotProgressService.getAll({ lot_id: id }),
        lotCode ? qmsCheckService.list({ lotto: lotCode }) : Promise.resolve({ data: [] }),
        nonConformityService.getAll({ lot_id: id }),
        attachmentService.getAll({ entity_type: 'production_lot', entity_id: id })
      ]);
      setProgress(progressRes.data);
      setControls(controlsRes.data);
      setNcs(ncsRes.data);
      setAttachments(attachmentsRes.data);

      try {
        const delRes = await deliveryService.getAll({ production_lot_id: id });
        setDeliveries(delRes.data || []);
      } catch { /* deliveries opzionali */ }

      try {
        const spRes = await simplifiedProgressService.getAll(id);
        setSimplifiedProgress(spRes.data || []);
      } catch { /* simplified opzionali */ }

      if (lotRes.data.product_revision_id) {
        const cyclesRes = await workCycleService.getAll({ product_revision_id: lotRes.data.product_revision_id });
        if (cyclesRes.data.length > 0) {
          const phasesRes = await workPhaseService.getAll({ work_cycle_id: cyclesRes.data[0].id });
          setWorkPhases(phasesRes.data);
        }
        const plansRes = await controlPlanService.getAll({ product_revision_id: lotRes.data.product_revision_id });
        setControlPlans(plansRes.data);
        const toolsRes = await revisionToolService.getAll({ product_revision_id: lotRes.data.product_revision_id });
        setRevisionTools(toolsRes.data);
      }
    } catch (error) {
      toast.error('Errore nel caricamento dettagli lotto');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadAttachments = useCallback(async () => {
    try {
      const res = await attachmentService.getAll({ entity_type: 'production_lot', entity_id: id });
      setAttachments(res.data);
    } catch { /* allegati opzionali */ }
  }, [id]);

  const fetchPrintData = async () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('evotech_token');
    const res = await fetch(`${API_URL}/api/production-lots/${id}/print-data`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Errore');
    return await res.json();
  };

  const handlePrintLot = async () => {
    try {
      const data = await fetchPrintData();
      setPrintData(data);
      setTriggerPrint(true);
    } catch (error) {
      toast.error('Errore nel caricamento dati di stampa');
    }
  };

  const handlePrintLotByPhases = async () => {
    try {
      const data = await fetchPrintData();
      setPrintData(data);
      setTriggerPhasePrint(true);
    } catch (error) {
      toast.error('Errore nel caricamento dati di stampa');
    }
  };

  useEffect(() => {
    loadLot();
  }, [loadLot]);

  // Auto-switch to simplified tab when simplified mode is active and no standard progress exists
  useEffect(() => {
    if (lot && (lot.use_simplified_progress || !lot.has_work_cycle) && progress.length === 0 && activeTab === 'progress') {
      setActiveTab('simplified');
    }
  }, [lot, progress.length, activeTab]);


  const handleAddProgress = async (e) => {
    e.preventDefault();
    try {
      await lotProgressService.create({
        lot_id: id,
        work_phase_id: progressForm.work_phase_id,
        operator: progressForm.operator || null,
        machine: progressForm.machine || null,
        good_quantity: parseInt(progressForm.good_quantity) || 0,
        scrap_quantity: parseInt(progressForm.scrap_quantity) || 0,
        rework_quantity: parseInt(progressForm.rework_quantity) || 0,
        actual_time_minutes: progressForm.actual_time_minutes ? parseInt(progressForm.actual_time_minutes) : null,
        notes: progressForm.notes || null,
      });
      toast.success('Avanzamento registrato');
      setProgressDialogOpen(false);
      setProgressForm(initialProgressForm);
      loadLot();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella registrazione avanzamento');
    }
  };

  const handleUpdateStatus = async () => {
    try {
      await productionLotService.update(id, { status: newStatus });
      toast.success('Stato aggiornato');
      setStatusDialogOpen(false);
      loadLot();
    } catch {
      toast.error("Errore nell'aggiornamento stato");
    }
  };

  const handleCreateDelivery = async () => {
    if (!deliveryForm.delivered_quantity || !lot.order_line_id) {
      toast.error('Compila quantita consegnata');
      return;
    }
    try {
      await deliveryService.create({
        production_lot_id: id,
        order_line_id: lot.order_line_id,
        delivery_date: deliveryForm.delivery_date,
        delivered_quantity: parseInt(deliveryForm.delivered_quantity),
        ddt_reference: deliveryForm.ddt_reference || null,
        notes: deliveryForm.notes || null,
      });
      toast.success('Consegna registrata');
      setDeliveryDialogOpen(false);
      setDeliveryForm(initialDeliveryForm());
      loadLot();
      const delRes = await deliveryService.getAll({ production_lot_id: id });
      setDeliveries(delRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella registrazione consegna');
    }
  };

  const handleDeleteDelivery = async (deliveryId) => {
    try {
      await deliveryService.delete(deliveryId);
      toast.success('Consegna eliminata');
      loadLot();
      const delRes = await deliveryService.getAll({ production_lot_id: id });
      setDeliveries(delRes.data || []);
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  const handleCreateSimplifiedProgress = async () => {
    const produced = parseInt(simpleForm.produced_quantity) || 0;
    const delivered = parseInt(simpleForm.delivered_quantity) || 0;
    if (produced === 0 && delivered === 0) {
      toast.error('Inserisci almeno una quantita prodotta o consegnata');
      return;
    }
    try {
      await simplifiedProgressService.create(id, {
        progress_date: simpleForm.progress_date,
        produced_quantity: produced,
        delivered_quantity: delivered,
        ddt_reference: simpleForm.ddt_reference || null,
        notes: simpleForm.notes || null,
      });
      toast.success('Avanzamento semplificato registrato');
      setSimpleDialogOpen(false);
      setSimpleForm(initialSimpleForm());
      loadLot();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore registrazione avanzamento');
    }
  };

  const handleDeleteSimplifiedProgress = async (progressId) => {
    try {
      await simplifiedProgressService.delete(progressId);
      toast.success('Avanzamento eliminato');
      loadLot();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione');
    }
  };

  const handleToggleSimplified = async (checked) => {
    try {
      await productionLotService.update(id, { use_simplified_progress: checked });
      toast.success(checked ? 'Modalita semplificata attivata' : 'Modalita standard attivata');
      loadLot();
    } catch {
      toast.error('Errore aggiornamento modalita');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!lot) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Production lot not found</p>
      </div>
    );
  }

  const hasWorkCycle = lot?.has_work_cycle ?? (workPhases.length > 0);
  const simplifiedActive = (lot?.use_simplified_progress) || !hasWorkCycle;
  const summary = lot?.delivery_summary || {
    ordered_quantity: lot?.quantity || 0,
    produced_quantity: lot?.good_quantity || 0,
    delivered_quantity: 0,
    remaining_quantity: lot?.quantity || 0,
    delivery_status: 'da_consegnare',
  };

  return (
    <div className="space-y-6 pb-24" data-testid="lot-detail-page">
      {/* Hidden print area */}
      <div style={{ display: 'none' }}>
        <div ref={printRef}>
          {printData && <PrintableLot data={printData} />}
        </div>
        <PrintablePhaseSheets ref={phasePrintRef} data={printData} />
      </div>

      <LotHeader
        lot={lot}
        hasRole={hasRole}
        onPrintLot={handlePrintLot}
        onPrintPhases={handlePrintLotByPhases}
        onChangeStatus={() => { setNewStatus(lot.status); setStatusDialogOpen(true); }}
        onOpenSimplified={() => setSimpleDialogOpen(true)}
        onOpenProgress={() => setProgressDialogOpen(true)}
        onGoToTab={setActiveTab}
        simplifiedActive={simplifiedActive}
        hasWorkPhases={workPhases.length > 0}
        controlsCount={controls.length}
        ncsCount={ncs.length}
      />

      <LotOverviewCards
        lot={lot}
        summary={summary}
        hasWorkCycle={hasWorkCycle}
        simplifiedActive={simplifiedActive}
        hasRole={hasRole}
        onToggleSimplified={handleToggleSimplified}
        onGoToNcTab={() => setActiveTab('ncs')}
      />

      <LotTabs
        lot={lot}
        lotId={id}
        activeTab={activeTab}
        onActiveTabChange={setActiveTab}
        simplifiedActive={simplifiedActive}
        simplifiedProgress={simplifiedProgress}
        progress={progress}
        workPhases={workPhases}
        controls={controls}
        ncs={ncs}
        revisionTools={revisionTools}
        deliveries={deliveries}
        attachments={attachments}
        hasRole={hasRole}
        onOpenSimpleDialog={() => setSimpleDialogOpen(true)}
        onDeleteSimpleProgress={handleDeleteSimplifiedProgress}
        onOpenProgressDialog={() => setProgressDialogOpen(true)}
        onOpenDeliveryDialog={() => setDeliveryDialogOpen(true)}
        onDeleteDelivery={handleDeleteDelivery}
        onLoadAttachments={loadAttachments}
      />

      <ProgressDialog
        open={progressDialogOpen}
        onOpenChange={setProgressDialogOpen}
        workPhases={workPhases}
        form={progressForm}
        setForm={setProgressForm}
        onSubmit={handleAddProgress}
      />
      <StatusDialog
        open={statusDialogOpen}
        onOpenChange={setStatusDialogOpen}
        newStatus={newStatus}
        setNewStatus={setNewStatus}
        onSubmit={handleUpdateStatus}
      />
      <DeliveryDialog
        open={deliveryDialogOpen}
        onOpenChange={setDeliveryDialogOpen}
        lot={lot}
        form={deliveryForm}
        setForm={setDeliveryForm}
        onSubmit={handleCreateDelivery}
      />
      <SimplifiedProgressDialog
        open={simpleDialogOpen}
        onOpenChange={setSimpleDialogOpen}
        lot={lot}
        form={simpleForm}
        setForm={setSimpleForm}
        onSubmit={handleCreateSimplifiedProgress}
      />
    </div>
  );
}
